import { useCallback, useEffect, useMemo, useReducer, useState, type ReactNode } from "react";

import {
  Forbidden as ForbiddenError,
  Offline as OfflineError,
  ServerError as ServerErrorResponse,
  Unauthorized,
} from "../api/client";
import { useBootstrap, useHealth, useRecent } from "../api/queries";
import { AppShell } from "../components/AppShell";
import { FilterSidebar } from "../components/FilterSidebar";
import { GlobalSearch } from "../components/GlobalSearch";
import { HealthBanner } from "../components/HealthBanner";
import { JumpNav } from "../components/JumpNav";
import { NoticeProvider, useNotice } from "../components/Notice";
import { RecalculateNow } from "../components/RecalculateNow";
import { RecentStrip } from "../components/RecentStrip";
import {
  DashboardSkeleton,
  Forbidden as ForbiddenScreen,
  Offline as OfflineScreen,
  ServerError as ServerErrorScreen,
} from "../components/States";
import { dashboardReducer, initialDashboardState } from "./reducer";
import {
  describeDropped,
  loadCharts,
  loadScopes,
  readUrlState,
  readView,
  saveCharts,
  saveScopes,
  writeUrlState,
  type Charts,
} from "./persist";
import { PanelScopeProvider } from "../panels/panelScope";
import { PANELS, specFor, type ChartKind, type PanelId } from "../panels/registry";
import { useKeyboardShortcuts } from "../lib/shortcuts";
import { schemaWarning } from "../lib/schemaWarning";
import { stampLabel, timeFormatOf } from "../lib/timeFormat";
import { CustomSection } from "../panels/CustomSection";
import { PanelSections } from "../panels/Sections";
import { Admin } from "../admin/Admin";
import { useAvailable } from "../wrapped/api";
import { Wrapped } from "../wrapped/Wrapped";
import { WrappedCard } from "../wrapped/WrappedCard";
import { Brand } from "../fork/Brand";
import { Login } from "./Login";

export default function App() {
  // The admin board is its own route with its own shell (§11.6), and there is no
  // router library by design. The path never changes without a reload, so this
  // stays a render-time branch and the dashboard keeps its hooks unconditional.
  // The notice region is the one thing both routes share: it is how a save, a
  // copy or a failure is acknowledged wherever it happened.
  const path = window.location.pathname;
  if (path.startsWith("/wrapped")) return <Wrapped />;
  return <NoticeProvider>{path.startsWith("/admin") ? <Admin /> : <Dashboard />}</NoticeProvider>;
}

function Dashboard() {
  const bootstrap = useBootstrap();
  const health = useHealth();
  const recent = useRecent(bootstrap.isSuccess);
  const wrapped = useAvailable(bootstrap.isSuccess);
  const wrappedTitle = wrapped.data?.released ? wrapped.data.title : null;
  // Everyone gets the way in once it's out; an admin gets it early to preview the draft.
  const wayIn = wrapped.data?.title && (wrapped.data.released || wrapped.data.draft)
    ? <WrappedCard title={wrapped.data.title} draft={!wrapped.data.released} />
    : null;

  const payload = bootstrap.data;

  if (bootstrap.isLoading) {
    return <DashboardSkeleton />;
  }
  if (bootstrap.error instanceof Unauthorized) {
    return <Login />;
  }
  if (bootstrap.error instanceof ForbiddenError) {
    return <ForbiddenScreen what="the dashboard" />;
  }
  if (bootstrap.error instanceof OfflineError) {
    return <OfflineScreen onRetry={() => void bootstrap.refetch()} />;
  }
  if (bootstrap.isError || !payload) {
    const detail =
      bootstrap.error instanceof ServerErrorResponse
        ? bootstrap.error.message
        : (bootstrap.error as Error)?.message;
    return (
      <ServerErrorScreen
        detail={detail}
        onRetry={() => void bootstrap.refetch()}
        // "Rollups are not built" is a 5xx an admin can actually clear, so the
        // screen offers the rebuild instead of a shell command.
        action={<RecalculateNow />}
      />
    );
  }

  // Mounted only once the payload exists: the reducer seeds `active` from the
  // author list, and seeding it from a placeholder left every chip reading
  // "off" while the charts still drew everybody.
  return (
    <DashboardBody payload={payload} banner={health.data?.banner ?? null}
                   lastIngestAt={health.data?.last_ingest_at ?? null}
                   recent={recent.data} wrappedTitle={wrappedTitle} wayIn={wayIn} />
  );
}

function DashboardBody({
  payload,
  banner,
  lastIngestAt,
  recent,
  wrappedTitle,
  wayIn,
}: {
  payload: NonNullable<ReturnType<typeof useBootstrap>["data"]>;
  banner: "stale" | "token_dead" | null;
  /** The worker's own stamp; null means it has never ingested anything. */
  lastIngestAt?: string | null;
  recent?: { messages: { author: string; created_at: string; body: string }[]; truncated?: boolean };
  /** Set once a Wrapped is released: the header link uses it. */
  wrappedTitle: string | null;
  /** The colourful card into Wrapped, first in the sidebar. */
  wayIn: ReactNode;
}) {
  // The sidebar starts from the link's `?view=`, so a reload or a shared link
  // keeps the years, dates and people it was showing.
  const [{ filters, scopes, mode }, dispatch] = useReducer(
    dashboardReducer,
    payload,
    (loaded) => initialDashboardState(loaded, readView(loaded)),
  );
  const years = [...new Set(payload.days.map((day) => Number(day.slice(0, 4))))].sort();
  const notify = useNotice();
  // `/` and `f`: the two things a reader reaches for on a page this long.
  useKeyboardShortcuts();
  // Stable, so a card's "…" does not re-render every panel through the context.
  const openScope = useCallback(
    (panelId: PanelId) => dispatch({ type: "openScope", panelId }),
    [dispatch],
  );

  // Saved scopes are read from the URL first (one entry per dimension, names
  // not indices) and the storage key second, then pruned against the payload
  // that is actually loaded.
  const [saved, setSaved] = useState(() => {
    const fromUrl = readUrlState(payload);
    const loaded = loadScopes(payload);
    return {
      scopes: { ...loaded.scopes, ...fromUrl.scopes },
      dropped: loaded.dropped,
    };
  });
  const [charts, setCharts] = useState<Charts>(() => loadCharts());

  // One card's shape, recorded beside the others. Choosing the shape the spec
  // already defaults to stores nothing, so "Reset view" reports only the cards
  // the reader has really changed.
  const setShape = useCallback((panelId: PanelId, shape: ChartKind) => {
    setCharts((previous) => {
      const next = { ...previous };
      if (shape === specFor(panelId).defaultShape) delete next[panelId];
      else next[panelId] = shape;
      return next;
    });
  }, []);

  useEffect(() => {
    for (const [panelId, patch] of Object.entries(saved.scopes)) {
      dispatch({ type: "replaceScope", panelId: panelId as PanelId, patch });
    }
  }, [dispatch, saved.scopes]);

  // Scopes are the one piece of state worth surviving a reload, and the URL is
  // what makes that work on a phone: it is the same view, not a re-typed one.
  useEffect(() => {
    saveScopes(scopes, payload.authors.map((author) => author.name));
  }, [payload, scopes]);

  // The address is the view: card scopes and the sidebar's own filters both.
  // Only the scopes go to localStorage too — a stale global filter waiting in
  // storage would make a fresh visit look broken rather than filtered.
  useEffect(() => {
    writeUrlState({ scopes, authors: payload.authors.map((author) => author.name), view: filters });
  }, [payload, scopes, filters]);

  useEffect(() => {
    saveCharts(charts);
  }, [charts]);

  // A reload or a shared link lands on its section. The browser looks for the
  // heading before the payload has drawn anything, finds none, and stays at the
  // top — which on a phone means scrolling all the way back down.
  // Called straight from the effect: the sections are in the document by now,
  // and an animation frame never comes in a tab the browser is not painting.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView?.({ block: "start" });
  }, []);

  // Built only when what the cards draw changes. The health poll (every minute)
  // and the sidebar switching modes re-render this component, and without the
  // memo every card handed its chart a new option and redrew all of them.
  const cards = useMemo(
    () => (
      // The sections in the spec's order, each card where its registry entry
      // puts it. Every card inside can hand its id to the sidebar, which is how
      // a card's "…" swaps what the sidebar edits.
      <PanelScopeProvider
        filters={filters}
        scopes={scopes}
        onOpenScope={openScope}
        charts={charts}
        onShapeChange={setShape}
      >
        <PanelSections payload={payload} filters={filters} dispatch={dispatch} />
        {/* Fork panels, rendered last. Upstream ships none, so this is silent. */}
        <CustomSection payload={payload} filters={filters} />
      </PanelScopeProvider>
    ),
    [payload, filters, scopes, openScope, charts, setShape, dispatch],
  );

  const pruned = describeDropped(saved.dropped, (panelId) =>
    panelId in PANELS ? PANELS[panelId as PanelId].label : panelId,
  );

  return (
    <AppShell
      canAdmin={payload.meta.can_admin}
      wrappedTitle={wrappedTitle}
      lead={
        <>
          <Brand />
          {wayIn}
        </>
      }
      mode={mode}
      sidebar={
        <FilterSidebar
          payload={payload}
          filters={filters}
          scopes={scopes}
          mode={mode}
          dispatch={dispatch}
          years={years}
          pruned={pruned}
          onDismissPruned={() => setSaved((previous) => ({ ...previous, dropped: [] }))}
          charts={charts}
          onExitScope={() => dispatch({ type: "closeScope" })}
          onResetView={() => {
            dispatch({ type: "clearScopes" });
            setCharts({});
            notify({ tone: "success", message: "Saved view cleared — every card is back to the global filters." });
          }}
        />
      }
      header={
        <>
          <span>
            {payload.meta.firstLocal} → {payload.meta.lastLocal} ·{" "}
            {filters.active.filter(Boolean).length} of {payload.authors.length} authors
          </span>
          <span>
            {payload.tz.label} · as of {stampLabel(payload.meta.computed_at, timeFormatOf(payload.meta))}
          </span>
          <GlobalSearch payload={payload} dispatch={dispatch} />
        </>
      }
    >
      <HealthBanner
        banner={banner}
        lastIngestAt={lastIngestAt}
        stale={payload.meta.stale}
        staleAt={payload.meta.computed_at}
        timeFormat={timeFormatOf(payload.meta)}
        timezoneMismatch={payload.meta.timezone_mismatch}
        timezoneMessage={payload.meta.timezone_message}
        // The versions disagree; `schemaWarning` tolerates a payload written
        // before this field existed, so nothing renders in that case.
        schemaMessage={schemaWarning(payload.meta)}
        // A viewer can read the warning but cannot queue the rebuild; only an
        // admin gets the control, so the button never leads to a 403.
        action={payload.meta.can_admin ? <RecalculateNow /> : null}
      />
      <JumpNav />
      <RecentStrip messages={recent?.messages ?? []} truncated={recent?.truncated} />
      {cards}
      <footer className="pb-8 text-[11px] text-[var(--color-muted)]">
        {payload.meta.payload_kb} KB payload ({payload.meta.payload_gzip_kb} KB gzipped) ·{" "}
        {payload.meta.cells.toLocaleString()} rollup cells instead of{" "}
        {payload.meta.source_count.toLocaleString()} message rows · {payload.meta.stopwords}{" "}
        stopwords
      </footer>
    </AppShell>
  );
}
